import { useEffect, useRef, useState } from 'react'
import { useEchoClient } from './echoClient'

/** Интервал опроса GET /dictionary-fill/{id}, когда WebSocket недоступен (страховка для read-only ученика). */
const DISCONNECTED_POLL_MS = 4000
/** Не опрашивать API, пока Echo может успеть подключиться после mount. */
const WS_CONNECT_GRACE_MS = 2500

export type DictionaryFillRealtimePayloads = {
  onItemAssessed?: (payload: {
    item_id: number
    verdict: string
    items_assessed: number
    cards_created: number
  }) => void
}

/**
 * Приватный канал dictionary-fill.{runId}: ученик видит оценки учителя
 * realtime, обе стороны узнают о новых батчах и завершении прогона.
 * При недоступном WS — polling-опрос через onUpdate (после grace-паузы).
 */
export function useDictionaryFillRealtime(
  runId: number | null,
  onUpdate: (type: 'assessed' | 'batch' | 'completed' | 'poll') => void,
  options?: DictionaryFillRealtimePayloads,
) {
  const [isConnected, setIsConnected] = useState(false)
  const onUpdateRef = useRef(onUpdate)
  const onItemAssessedRef = useRef(options?.onItemAssessed)

  onUpdateRef.current = onUpdate
  onItemAssessedRef.current = options?.onItemAssessed

  // Клиент — зависимость эффекта: после пересоздания Echo подписка иначе
  // осталась бы на отключённом сокете.
  const echo = useEchoClient()

  useEffect(() => {
    if (!Number.isFinite(runId)) {
      setIsConnected(false)
      return
    }

    const channelName = `dictionary-fill.${runId}`
    const privateKey = `private-${channelName}`

    setIsConnected(false)

    const connector = echo.connector as { channels?: Record<string, unknown> }
    if (connector.channels?.[privateKey]) {
      try {
        echo.leave(channelName)
      } catch {
        // ignore
      }
    }

    const channel = echo.private(channelName)

    type ChannelWithLifecycle = typeof channel & {
      subscribed?: (cb: () => void) => typeof channel
      error?: (cb: (err: unknown) => void) => typeof channel
    }
    const ch = channel as ChannelWithLifecycle

    if (typeof ch.subscribed === 'function') {
      ch.subscribed(() => setIsConnected(true))
    } else {
      setIsConnected(true)
    }

    if (typeof ch.error === 'function') {
      ch.error((err) => {
        setIsConnected(false)
        console.error('[Echo] Ошибка подписки на канал', channelName, err)
      })
    }

    channel.listen('.fill.item.assessed', (raw: unknown) => {
      const p = raw as {
        item_id?: unknown
        verdict?: unknown
        items_assessed?: unknown
        cards_created?: unknown
      }
      const item_id = Number(p.item_id)
      if (Number.isFinite(item_id)) {
        onItemAssessedRef.current?.({
          item_id,
          verdict: String(p.verdict ?? ''),
          items_assessed: Number(p.items_assessed ?? 0),
          cards_created: Number(p.cards_created ?? 0),
        })
      }
      onUpdateRef.current('assessed')
    })
    channel.listen('.fill.batch.appended', () => onUpdateRef.current('batch'))
    channel.listen('.fill.run.completed', () => onUpdateRef.current('completed'))

    return () => {
      try {
        echo.leave(channelName)
      } catch {
        // ignore
      }
      setIsConnected(false)
    }
  }, [echo, runId])

  useEffect(() => {
    if (!Number.isFinite(runId) || isConnected) return

    let intervalId: ReturnType<typeof window.setInterval> | undefined
    const graceTimer = window.setTimeout(() => {
      onUpdateRef.current('poll')
      intervalId = window.setInterval(() => {
        onUpdateRef.current('poll')
      }, DISCONNECTED_POLL_MS)
    }, WS_CONNECT_GRACE_MS)

    return () => {
      window.clearTimeout(graceTimer)
      if (intervalId !== undefined) {
        window.clearInterval(intervalId)
      }
    }
  }, [runId, isConnected])

  return { isConnected }
}
